import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Switch, Checkbox, Box, Typography } from "@mui/material";
import ReusableGrid from "../../../components/ag grid/ReusableGrid";
import { BRAND } from "../../../components/auth/authTheme";

const PAGE_SIZE = 10;
const ROW_HEIGHT = 48;
const HEADER_HEIGHT = 44;
const PAGINATION_HEIGHT = 52;
const GRID_HEIGHT =
  ROW_HEIGHT * PAGE_SIZE + HEADER_HEIGHT + PAGINATION_HEIGHT;

/** Primary permission columns shown in the grid */
const DISPLAY_ACTIONS = ["view", "create", "edit", "delete"];

const EmployerPermissionsTab = ({
  modulesData,
  permissions,
  setPermissions,
  basePermissions = [],
}) => {
  const gridRef = useRef();
  const [allActions, setAllActions] = useState([]);

  useEffect(() => {
    if (!modulesData) return;

    const keys = new Set();
    modulesData.forEach((mod) => {
      mod.actions.forEach((act) => keys.add(act.key));
    });
    setAllActions([...keys]);
  }, [modulesData]);

  const visibleActions = useMemo(
    () => DISPLAY_ACTIONS.filter((action) => allActions.includes(action)),
    [allActions]
  );

  const allActionsRef = useRef(allActions);
  allActionsRef.current = allActions;

  const isBasePermission = useCallback((module, action) => {
    if (!basePermissions || basePermissions.length === 0) return false;
    const baseMod = basePermissions.find(b => b.module === module);
    if (!baseMod) return false;
    if (action === "enabled") return baseMod.enabled;
    return Boolean(baseMod[action]);
  }, [basePermissions]);

  const handleToggle = useCallback((module, field, value) => {
    setPermissions((prev) => {
      const updatedData = prev.map((row) => {
        if (row.module !== module) return row;

        if (field === "enabled") {
          const updated = { ...row, enabled: value };
          allActionsRef.current.forEach((action) => {
            updated[action] = value ? action === "view" : false;
          });
          return updated;
        }

        return { ...row, [field]: value };
      });

      setTimeout(() => {
        const api = gridRef.current;
        if (api?.ensureIndexVisible) {
          const rowIndex = updatedData.findIndex((r) => r.module === module);
          if (rowIndex >= 0) api.ensureIndexVisible(rowIndex, "middle");
        }
      }, 0);

      return updatedData;
    });
  }, [setPermissions]);

  const actionRenderer = useCallback(
    (field) => (params) => {
      const isBase = isBasePermission(params.data.module, field);
      return (
        <Checkbox
          checked={Boolean(params.data[field]) || isBase}
          onChange={(e) =>
            handleToggle(params.data.module, field, e.target.checked)
          }
          disabled={(!params.data.enabled && !isBasePermission(params.data.module, 'enabled')) || isBase}
          size="small"
          sx={{
            color: BRAND.border,
            "&.Mui-checked": { color: BRAND.orange },
            "&.Mui-disabled": { opacity: 0.35 },
          }}
        />
      );
    },
    [handleToggle, isBasePermission]
  );

  const columnDefs = useMemo(() => {
    const moduleColumn = {
      headerName: "Module",
      field: "moduleLabel",
      pinned: "left",
      cellClass: "module-permission-cell wrap-text",
      minWidth: 220,
      flex: 1.5,
      suppressSizeToFit: true,
      cellRenderer: (params) => {
        const isBaseEnabled = isBasePermission(params.data.module, "enabled");
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
            <Switch
              size="small"
              checked={Boolean(params.data.enabled) || isBaseEnabled}
              onChange={(e) =>
                handleToggle(params.data.module, "enabled", e.target.checked)
              }
              disabled={isBaseEnabled}
            color="primary"
          />
          <Typography variant="body2" sx={{ fontWeight: 500, color: BRAND.text }}>
            {params.data.moduleLabel}
          </Typography>
        </Box>
        );
      },
    };

    const actionColumns = visibleActions.map((action) => ({
      headerName: action
        .replace(/_/g, " ")
        .replace(/\b\w/g, (l) => l.toUpperCase()),
      field: action,
      width: 90,
      minWidth: 80,
      maxWidth: 100,
      suppressSizeToFit: true,
      headerClass: "permission-action-header",
      cellClass: "permission-action-cell",
      cellRenderer: actionRenderer(action),
    }));

    return [moduleColumn, ...actionColumns];
  }, [visibleActions, handleToggle, actionRenderer]);

  const gridDefaultColDef = useMemo(
    () => ({
      sortable: false,
      resizable: false,
      minWidth: 80,
    }),
    []
  );

  return (
    <Box>
      <Typography variant="body2" sx={{ color: BRAND.muted, mb: 1.5 }}>
        Toggle a module to enable permissions, then select allowed actions.
        Showing {PAGE_SIZE} modules per page.
      </Typography>

      <Box className="permission-grid-wrapper list-grid-container">
        <ReusableGrid
          rowData={permissions}
          columnDefs={columnDefs}
          pagination={true}
          gridRef={gridRef}
          domLayout="normal"
          height={`${GRID_HEIGHT}px`}
          pageSize={PAGE_SIZE}
          defaultColDef={gridDefaultColDef}
          emptyMessage="No permission modules available"
        />
      </Box>
    </Box>
  );
};

export default EmployerPermissionsTab;
